import type { BlobModel } from '@/models/blob';
import { FormModel, type NetworkForm, type PatchFormsOrder, type PostForm, type PostFormFile,
  type FormFileSignedResponse, type PresignedPostFields, type PresignedPost, 
  type PostFormResponse} from '@/models/form';
import { signedApiClient } from '@/services/apiClient';
import axios, { type AxiosProgressEvent } from 'axios';

class FormService {

  private investiationEndpoint: string = '';
  private surveyEndpoint: string = '';
  initService(investiationEndpoint: string, surveyEndpoint: string) {
    this.investiationEndpoint = investiationEndpoint;
    this.surveyEndpoint = surveyEndpoint;
  }

  async getStudyForms(id: string, caseId?: string | string[]) {
    let uri = `${this.investiationEndpoint}/${id}/surveys`;
    if (caseId) {
      uri += `?caseId=${caseId}`;
    }
    const apiClient = signedApiClient('GET', uri);
    const exportsResponse = await apiClient.get(uri);
    const formList: NetworkForm[] = exportsResponse.data.surveys;
    return formList.map(form => FormModel.mapFromBackend(form));
  }

  async deleteForm(id: string) {
    const uri = `${this.surveyEndpoint}/${id}`;
    const apiClient = signedApiClient('DELETE', uri);
    return await apiClient.delete(uri);
  }

  async cloneForm(id: string) {
    const uri = `${this.surveyEndpoint}/${id}/clone`;
    const apiClient = signedApiClient('POST', uri);
    return await apiClient.post(uri);
  }

  async patchFormsOrder(formsOrder: PatchFormsOrder) {
    const uri = `${this.surveyEndpoint}/enforcement`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, formsOrder);
  }

  async getForm(identifier: string, caseIdentifier?: string | null, studyId?: string, token?: string) {
    let uri = `form/${identifier}`;
    if (caseIdentifier) {
      uri += `?caseId=${caseIdentifier}`;
      if (studyId) {
        uri += `&studyId=${studyId}`;
      }
      if (token) {
        uri += `&token=${token}`;
      }
    } else if (studyId) {
      uri += `?studyId=${studyId}`;
      if (token) {
        uri += `&token=${token}`;
      }
    }
    const apiClient = signedApiClient('GET', uri);
    const formResponse = await apiClient.get(uri);
    return FormModel.mapFromBackend(formResponse.data);
  }

  async postForm(form: PostForm): Promise<PostFormResponse> {
    const uri = `form`;
    const apiClient = signedApiClient('POST', uri);
    return (await apiClient.post(uri, form)).data;
  }

  async patchForm(id: string, form: PostForm) {
    const uri = `form/${id}`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, form);
  }

  async postFormFile(id: string, file: PostFormFile): Promise<FormFileSignedResponse> {
    const uri = `${this.surveyEndpoint}/${id}/file`;
    const apiClient = signedApiClient('POST', uri);
    return (await apiClient.post(uri, file)).data;
  }

  async uploadFileToS3(file: File, presignedData: PresignedPost) {
    const fields = presignedData.fields;
    const form = new FormData();

    (Object.keys(fields) as Array<keyof PresignedPostFields>).forEach(key => {
      form.append(key, fields[key] as string);
    });

    form.append('file', file);

    const res = await axios({
      url: presignedData.url,
      method: 'POST',
      data: form
    });
    return res.data;
  }

  async uploadFileToS3WithProgress(file: BlobModel, presignedData: PresignedPost) {
    const fields = presignedData.fields;
    const form = new FormData();

    (Object.keys(fields) as Array<keyof PresignedPostFields>).forEach(key => {
      form.append(key, fields[key] as string);
    });

    form.append('file', file.file!);

    const res = await axios({
      url: presignedData.url,
      method: 'POST',
      data: form,
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (progressEvent && progressEvent.lengthComputable) {
          const progress = Math.round((progressEvent.loaded / progressEvent.total!) * 100);
          file.progress = progress;
        }
      }
    });
    return res.data;
  }
}

export default new FormService();
